// src/components/InKindServiceForm.js
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import ReactSelect from "react-select";
import { toast } from "react-toastify";
import {
  getCitiesByState,
  getLocalityByState,
  getServicesTypes,
  getStates,
  getUnits,
  getVehicleTypes,
} from "../api";
import { api_url } from "../constant";
import AppBar from "./appBar";
import Client from "../utils/client";
import ReactGA from "react-ga4";

const InKindServiceForm = ({ contributionType = "donate" }) => {
  ReactGA.send({
    hitType: "pageview",
    page: window.location.pathname,
    title: `In-Kind Service ${contributionType === "donate" ? "Donation" : "Request"}`,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    getValues,
    setValue,
    reset,
  } = useForm();

  const navigate = useNavigate();
  const watchAll = watch();

  const { data: units } = useQuery({
    queryKey: ["units"],
    queryFn: () => getUnits(),
    cacheTime: 99999,
    staleTime: 99999,
  });

  const { data: states } = useQuery({
    queryKey: ["states"],
    queryFn: () => getStates(),
    initialData: [],
  });

  const { data: services, isFetching } = useQuery({
    queryKey: ["services"],
    queryFn: () => getServicesTypes(),
    initialData: [],
  });

  const { data: vehicle_types } = useQuery({
    queryKey: ["vehicle_types"],
    queryFn: () => getVehicleTypes(),
    initialData: [],
  });

  const { data: cities, refetch: citiesRefetch } = useQuery({
    queryKey: ["cities"],
    queryFn: () => getCitiesByState(getValues().state?.value),
    initialData: [],
    enabled: false,
  });

  const { data: localities, refetch: localitiesRefetch } = useQuery({
    queryKey: ["localities"],
    queryFn: () => getLocalityByState(getValues().state?.value),
    initialData: [],
    enabled: false,
  });

  const onSubmit = (data) => post(data);

  const post = async (data) => {
    // Validation
    if (!data.service_id || !data.service_id.value) {
      toast("يرجى اختيار الخدمة", { type: "error" });
      return;
    }
    if (!data.state || !data.state.value) {
      toast("يرجى اختيار الولاية", { type: "error" });
      return;
    }
    if (!data.locality || !data.locality.value) {
      toast("يرجى اختيار المحلية", { type: "error" });
      return;
    }
    if (!data.location) {
      toast("يرجى تحديد الموقع التفصيلي", { type: "error" });
      return;
    }
    if (!data.description) {
      toast("يرجى كتابة وصف الخدمة", { type: "error" });
      return;
    }

    const _data = {
      service_id: data.service_id.value,
      city: data.city?.value || null,
      locality: data.locality.value,
      state: data.state.value,
      description: data.description,
      location: data.location,
      // Service-specific fields
      price_per_day: null, // Always null for in-kind
      quantity: data.quantity || null,
      vehicle_type_id: data.vehicle_type_id?.value || null,
      type: data.type || null,
      payload: data.payload ? data.payload.toString() : null,
      line: data.line || null,
      space: data.space || null,
      temperature: data.temperature || null,
      height: data.height || null,
      number_of_workers: data.number_of_workers || null,
      service_type: contributionType, // "donate" or "need"
    };

    try {
      const response = await Client.post(api_url + "/services/in-kind", _data, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
          "Content-Type": "application/json",
        },
      });

      if (response.data && response.data.id) {
        const successMessage = contributionType === "donate"
          ? "تم نشر التبرع بالخدمة بنجاح"
          : "تم نشر طلب الخدمة بنجاح";
        toast(successMessage, { type: "success" });

        setTimeout(() => {
          window.history.go(-1);
        }, 1500);
      } else {
        throw new Error("فشل في إنشاء المساهمة العينية");
      }
    } catch (error) {
      console.error("Error:", error);
      toast("الرجاء التأكد من البيانات", { type: "error" });
    }
  };

  return (
    <div className="items-center justify-center w-screen" dir="rtl">
      <div className="h-10" />

      {/* Header */}
      <div className="flex flex-row justify-between px-40 font-bold text-primary text-[18px]">
        <div
          className="flex justify-center items-center cursor-pointer"
          onClick={() => window.history.go(-1)}
        >
          <svg
            className="w-7 text-primary"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.75 19.5L8.25 12l7.5-7.5"
            />
          </svg>
          <p>العودة إلى القائمة</p>
        </div>
        <div className="text-black">
          {contributionType === "donate" ? "تبرع بخدمة" : "طلب خدمة عينية"}
        </div>
      </div>

      <div className="h-10"></div>

      {/* Info Box */}
      <div className="px-40 mb-6">
        <div className={`p-4 rounded-lg ${contributionType === "donate"
            ? "bg-green-50 border border-green-200"
            : "bg-blue-50 border border-blue-200"
          }`}>
          <p className={`font-bold ${contributionType === "donate" ? "text-green-800" : "text-blue-800"
            }`}>
            {contributionType === "donate"
              ? "💚 شارك بخدماتك الزراعية مع من يحتاجها"
              : "💙 اطلب الخدمات الزراعية التي تحتاجها"
            }
          </p>
          <p className={`text-sm mt-1 ${contributionType === "donate" ? "text-green-700" : "text-blue-700"
            }`}>
            {contributionType === "donate"
              ? "ساهم في دعم مجتمعك من خلال تقديم خدمات الزراعية"
              : "احصل على الدعم من المانحين لتغطية احتياجاتك من الخدمات الزراعية"
            }
          </p>
        </div>
      </div>

      <div className="flex flex-col">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col items-center justify-center gap-5"
        >
          {/* Service Type Selection */}
          <div className="flex flex-col w-2/6 lg:w-96">
            <ReactSelect
              options={!isFetching && services.map((e) => {
                return { label: e.name, value: e.id };
              })}
              value={getValues().service_id}
              onChange={(value) => reset({ ...getValues(), service_id: value })}
              hideSelectedOptions={false}
              closeMenuOnSelect={true}
              isRtl
              placeholder={
                <span className="font-bold after:content-['*'] after:mr-1 after:text-red-500">
                  الخدمة
                </span>
              }
            />
          </div>

          {/* Location Selection */}
          <div className="flex flex-row-reverse gap-5 w-2/6 lg:w-96">
            <div className="w-full">
              <ReactSelect
                options={states?.map((e) => {
                  return { label: e.name, value: e.id };
                })}
                value={getValues().state}
                onChange={(value) => {
                  setValue("state", value);
                  citiesRefetch();
                  localitiesRefetch();
                }}
                hideSelectedOptions={false}
                closeMenuOnSelect={true}
                isRtl
                placeholder={
                  <span className="font-bold after:content-['*'] after:mr-1 after:text-red-500">
                    الولاية
                  </span>
                }
              />
            </div>
            <div className="w-full">
              <ReactSelect
                options={localities?.map((e) => {
                  return { label: e.name, value: e.id };
                })}
                value={getValues().locality}
                onChange={(value) => reset({ ...getValues(), locality: value })}
                hideSelectedOptions={false}
                closeMenuOnSelect={true}
                isRtl
                placeholder={
                  <span className="font-bold after:content-['*'] after:mr-1 after:text-red-500">
                    المحلية
                  </span>
                }
                isDisabled={localities?.length === 0}
              />
            </div>
          </div>

          {/* Detailed Location Input */}
          <div className="w-96">
            <input
              {...register("location", { required: true })}
              className="border-2 h-12 p-3 rounded-md w-full"
              placeholder="الموقع التفصيلي (القرية، المنطقة، المعلم...)*"
              dir="rtl"
            />
          </div>

          {/* Description */}
          <textarea
            {...register("description", { required: true })}
            className="h-32 p-3 rounded-md w-96"
            placeholder={
              contributionType === "donate"
                ? "وصف الخدمة المتبرع بها، مثلا: (أتاح جرار زراعي لحراثة الأراضي للمزارعين المحتاجين في المنطقة)*"
                : "وصف الخدمة المطلوبة والسبب، مثلا: (أحتاج لعمالة لحصاد محصول القمح في مزرعتي بسبب نقص الأيدي العاملة)*"
            }
            dir="rtl"
          />

          {/* Dynamic Fields Based on Service Type */}

          {/* Labor Service (ID 3) */}
          {watchAll.service_id && watchAll.service_id.value === 3 && (
            <div className="flex gap-5 w-96">
              <div className="flex flex-col w-2/6 lg:w-96">
                <ReactSelect
                  options={[
                    { label: "يومية", value: "يومية" },
                    { label: "مقاولة", value: "مقاولة" },
                  ]}
                  value={{
                    label: getValues().type ?? "نوع العمالة",
                    value: getValues().type,
                  }}
                  onChange={(value) =>
                    reset({ ...getValues(), type: value.value })
                  }
                  hideSelectedOptions={false}
                  closeMenuOnSelect={true}
                  isRtl
                  placeholder="نوع العمالة"
                />
              </div>
              <input
                type="number"
                {...register("number_of_workers")}
                className="border-2 h-10 p-3 rounded-md w-full"
                placeholder="عدد العمال"
              />
            </div>
          )}

          {/* Storage Service (ID 2) */}
          {watchAll.service_id && watchAll.service_id.value === 2 && (
            <>
              <div className="flex gap-5 w-96">
                <input
                  type="number"
                  {...register("temperature")}
                  className="border-2 border-gray-300 h-10 p-3 rounded-md w-full"
                  placeholder="درجة الحرارة"
                />
                <div className="flex flex-col w-2/6 lg:w-96">
                  <ReactSelect
                    options={[
                      { label: "جاف", value: "جاف" },
                      { label: "مبرد", value: "مبرد" },
                    ]}
                    value={{
                      label: getValues().type ?? "نوع المخزن",
                      value: getValues().type,
                    }}
                    onChange={(value) =>
                      reset({ ...getValues(), type: value.value })
                    }
                    hideSelectedOptions={false}
                    closeMenuOnSelect={true}
                    isRtl
                    placeholder="نوع المخزن"
                  />
                </div>
              </div>
              <div className="flex gap-5 w-96">
                <input
                  {...register("height")}
                  className="border-2 h-12 p-3 rounded-md w-full"
                  placeholder="الإرتفاع م"
                />
                <input
                  {...register("space")}
                  className="border-2 h-12 p-3 rounded-md w-full"
                  placeholder="المساحة م"
                />
              </div>
            </>
          )}

          {/* Transport Service (ID 1) */}
          {watchAll.service_id && watchAll.service_id.value === 1 && (
            <>
              <div className="flex flex-row-reverse gap-5 w-2/6 lg:w-96">
                <div className="flex flex-col w-2/6 lg:w-96">
                  <ReactSelect
                    options={vehicle_types?.map((e) => {
                      return {
                        label: e.name + " " + (e.payload || ""),
                        value: e.id,
                      };
                    })}
                    value={getValues().vehicle_type_id}
                    onChange={(value) => reset({ ...getValues(), vehicle_type_id: value })}
                    hideSelectedOptions={false}
                    closeMenuOnSelect={true}
                    isRtl
                    placeholder="نوع المركبة"
                  />
                </div>
                <div className="flex flex-col w-2/6 lg:w-96">
                  <ReactSelect
                    options={[
                      { label: "محلي", value: "محلي" },
                      { label: "ولائي", value: "ولائي" },
                    ]}
                    value={{
                      label: getValues().line ?? "خط النقل",
                      value: getValues().line,
                    }}
                    onChange={(value) =>
                      reset({ ...getValues(), line: value.value })
                    }
                    hideSelectedOptions={false}
                    closeMenuOnSelect={true}
                    isRtl
                    placeholder="خط النقل"
                  />
                </div>
              </div>

              <div className="flex gap-5 w-96">
                <div className="flex flex-col w-2/6 lg:w-96">
                  <ReactSelect
                    options={units?.map((e) => {
                      return { label: e.name, value: e.name };
                    })}
                    value={{
                      label: getValues().quantity ?? "الوحدة",
                      value: getValues().quantity,
                    }}
                    onChange={(value) =>
                      reset({ ...getValues(), quantity: value.value })
                    }
                    hideSelectedOptions={false}
                    closeMenuOnSelect={true}
                    isRtl
                    placeholder="الوحدة"
                  />
                </div>
                <input
                  type="number"
                  min="1"
                  {...register("payload", {
                    valueAsNumber: true,
                  })}
                  className="border-[1px] border-gray-400 h-10 p-3 rounded-md w-full"
                  placeholder="الحمولة"
                />
              </div>
            </>
          )}

          {/* Donation Capacity - Only for donations */}
          {contributionType === "donate" && (
            <div className="w-96">
              <input
                {...register("donation_capacity")}
                className="border-2 h-12 p-3 rounded-md w-full"
                placeholder="سعة التبرع (مثال: 100 طن تخزين، 50 رحلة نقل، 200 ساعة عمل)"
                dir="rtl"
              />
              <p className="text-xs text-gray-500 text-right mt-1">
                اذكر السعة الإجمالية للتبرع مع الوحدة
              </p>
            </div>
          )}

          {/* Submit Button */}
          <input
            type="submit"
            className={`h-12 text-white rounded-md w-60 font-bold cursor-pointer ${contributionType === "donate"
                ? "bg-green-600 hover:bg-green-700"
                : "bg-blue-600 hover:bg-blue-700"
              } transition-colors`}
            value={contributionType === "donate" ? "تبرع بالخدمة" : "اطلب الخدمة"}
          />
        </form>
      </div>

      <div className="h-20" />
    </div>
  );
};

export default InKindServiceForm;